import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  useTheme,
} from "@mui/material";
import { VendorPOC } from "../../../models/Vendors/VendorPOC";
import { StyledTableCell } from "../../../styles/Shipmentstyles";
import FormTitle from "../../components/common/form/FormTitle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { useLocation } from "react-router-dom";
import { useStores } from "../../../use-stores";
import { useNotification } from "../../../utils/NotificationsContext";
import { runInAction } from "mobx";

interface PocListingProps {
  pocs: VendorPOC[];
}

export const PocListing: React.FC<PocListingProps> = ({ pocs }) => {
  const { vendorStore } = useStores();
  const theme = useTheme();
  const { showError, showSuccess } = useNotification();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const vendorId = Number(queryParams.get("vendor-id"));

  const isPrimaryPoc = (poc: VendorPOC) => {
    return (
      poc.is_primary === "1" ||
      String(poc.is_primary) === "1"
    );
  };

  const setPrimaryPoc = (poc: VendorPOC, index: number) => {
    if (isPrimaryPoc(poc)) {
      return;
    }

    if (vendorStore.vendorDetails?.poc_list) {
      runInAction(() => {
        vendorStore.vendorDetails!.poc_list = vendorStore.vendorDetails!.poc_list!.map((p, i) => {
          const { is_primary, ...restPoc } = p;
          return {
            ...restPoc,
            is_primary: i === index ? "1" : "0",
          };
        });
      });
    }
  };

  const deletePoc = (poc: VendorPOC, index: number) => {
    if (poc.id) {
      vendorStore
        .deleteVendorPoc(vendorId, poc.id)
        .then(() => {
          showSuccess("POC deleted successfully");
        })
        .catch((error) => {
          showError(
            "Error occurred",
            error?.response?.data?.message ||
            "Something went wrong! Please try again later."
          );
        });
    } else {
      vendorStore.removeLocalPoc(index);
      showSuccess("POC deleted successfully");
    }
  };

  const sortedPocs = [...(pocs || [])].sort((a, b) => {
    const aIsPrimary = isPrimaryPoc(a);
    const bIsPrimary = isPrimaryPoc(b);
    if (aIsPrimary && !bIsPrimary) return -1;
    if (!aIsPrimary && bIsPrimary) return 1;
    return 0;
  });
  return (
    <>
      {pocs && pocs.length > 0 ? (
        <>
          <FormTitle title="POC Listing" />
          <TableContainer sx={{ maxHeight: "222px", overflow: "auto" }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <StyledTableCell
                    sx={{
                      padding: "0",
                      textAlign: "center",
                      zIndex: 10,
                      fontSize: "12px",
                    }}
                  >
                    Name
                  </StyledTableCell>

                  <StyledTableCell
                    sx={{
                      padding: "0",
                      textAlign: "center",
                      zIndex: 10,
                      fontSize: "12px",
                    }}
                  >
                    Email
                  </StyledTableCell>

                  <StyledTableCell
                    sx={{
                      padding: "0",
                      textAlign: "center",
                      zIndex: 10,
                      fontSize: "12px",
                    }}
                  >
                    Phone
                  </StyledTableCell>
                  <StyledTableCell
                    sx={{
                      padding: "0",
                      textAlign: "center",
                      zIndex: 10,
                      fontSize: "12px",
                    }}
                  >
                    Status
                  </StyledTableCell>
                  <StyledTableCell
                    sx={{
                      padding: "0",
                      textAlign: "center",
                      zIndex: 10,
                      fontSize: "12px",
                    }}
                  >
                    Delete
                  </StyledTableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {sortedPocs.map((poc, i) => {
                  const originalIndex = pocs.findIndex(p =>
                    (p.id && poc.id && p.id === poc.id) ||
                    (!p.id && !poc.id && p === poc)
                  );
                  const isPrimary = isPrimaryPoc(poc);
                  return (
                    <TableRow
                      key={i}
                      onClick={() => !isPrimary && setPrimaryPoc(poc, originalIndex >= 0 ? originalIndex : i)}
                      sx={{
                        cursor: isPrimary ? "default" : "pointer",
                        backgroundColor: isPrimary ? theme.palette.primary.main + "08" : "transparent",
                        "&:hover": {
                          backgroundColor: isPrimary ? theme.palette.primary.main + "08" : theme.palette.action.hover,
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          padding: "8px",
                          textAlign: "center",
                          fontSize: "12px",
                          cursor: "default",
                        }}
                        title={poc.first_name + " " + poc.last_name}
                      >
                        {poc.first_name + " " + poc.last_name || "--"}
                      </TableCell>

                      <TableCell
                        sx={{
                          padding: "8px",
                          textAlign: "center",
                          fontSize: "12px",
                          cursor: "default",
                        }}
                        title={poc.email}
                      >
                        {poc.email || "--"}
                      </TableCell>

                      <TableCell
                        sx={{
                          padding: "8px",
                          textAlign: "center",
                          fontSize: "12px",
                          cursor: "default",
                        }}
                        title={poc.phone}
                      >
                        {poc.phone || "--"}
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "8px",
                          textAlign: "center",
                          fontSize: "12px",
                          cursor: "default",
                        }}
                      >
                        {isPrimary ? (
                          <Chip
                            label="Primary"
                            size="small"
                            sx={{
                              backgroundColor: theme.palette.primary.main + "15",
                              color: theme.palette.primary.main,
                              fontWeight: 600,
                            }}
                          />
                        ) : (
                          <Chip
                            label="Click to set as Primary"
                            size="small"
                            sx={{
                              backgroundColor: theme.palette.grey[200],
                              color: theme.palette.text.secondary,
                              fontSize: "10px",
                              cursor: "pointer",
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "8px",
                          textAlign: "center",
                          fontSize: "12px",
                          cursor: "default",
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FontAwesomeIcon
                          style={{ color: "red", cursor: "pointer" }}
                          icon={faTrash}
                          onClick={() => deletePoc(poc, originalIndex >= 0 ? originalIndex : i)}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : (
        <></>
      )}
    </>
  );
};
